import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { RadioButton, RadioButtonChangeParams } from "primereact/radiobutton"
import { InputTextarea } from "primereact/inputtextarea"
import { Message } from "primereact/message"
import commonStyles from "../../common.module.css"
import { useNavigate, useParams } from "react-router-dom"
import { OrderParams } from "."
import { useUpdateCalculation } from "../../../api/queries/useUpdateCalculation"
import { ChangeEvent, ReactNode, SyntheticEvent, useContext, useEffect, useState } from "react"
import { LoadingTerm, LoadingTermsResponseData, OrderCalculation, OrderCalculationCod, UnloadingTerm, UnloadingTermsResponseData, UnloadingTimesResponseData, UpdateOrderCalculationPaylod, UpdateOrderCalculationResponseData } from "../../../api/types/order"
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { validateCalculation, validateCalculationCod } from "./validations/calculation"
import FieldError, { FormErrors } from "../../FieldError"
import { classNames } from "primereact/utils"
import { Dropdown, DropdownChangeParams, DropdownProps } from "primereact/dropdown"
import moment from "moment"
import { useLoadingTerms } from "../../../api/queries/useLoadingTerms"
import { formatDate } from "../../../common/datetime"
import { TimeRange } from "../../common/TimeRange"
import { validateIn, validateTimeRange, validateVatId } from "./validations/common"
import { useUnloadingTerms } from "../../../api/queries/useUnloadingTerms"
import { useUnloadingTimes } from "../../../api/queries/useUnloadingTimes"
import { isOrderEditable } from "../StateTag"
import { OrderResponseError } from "../../../api/types/base"
import { usePrompt } from "../../../common/usePrompt"
import styles from "./styles.module.css"
import userContext from "../../../context/user"
import { useAddress } from "../../../api/queries/useAddress"
import { useAddresses } from "../../../api/queries/useAddresses"
import { Address } from "../../../api/types/address"
import { Collapsible } from "../../common/Collapsible"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { useAddressBookDetail } from "../../../api/queries/address-book/useAddressBookDetail"

const TIME_OPTION = {
  CUSTOM: "CUSTOM",
  TBA: "TBA",
}

const DATE_OPTION = {
  CLOSEST: "CLOSEST"
}

export default function OrderFormCalculation() {
  const today = moment().format("YYYY-MM-DD")
  const { uuid } = useParams<OrderParams>()
  const navigate = useNavigate()
  const updateCalculation = useUpdateCalculation(uuid)
  const { data: orderDetail } = useOrderDetail(uuid)
  const { user } = useContext(userContext)
  const { data: loadingAddressBook } = useAddressBookDetail(orderDetail?.results.orderDetail.loading?.addressBookUuid || "")
  const { data: unloadingAddressBook } = useAddressBookDetail(orderDetail?.results.orderDetail.unloading?.addressBookUuid || "")

  const { data: billingAddress } = useAddress(user?.billingAddress || undefined)
  const { data: mainAddress } = useAddresses({ limit: 0, offset: 0, fIsMain: true })

  const isEditable = isOrderEditable(orderDetail?.results.orderDetail.state || 0)

  const [errors, setErrors] = useState<FormErrors<OrderCalculation>>({})
  const [errorsCod, setErrorsCod] = useState<FormErrors<OrderCalculationCod>>({})
  const [reference, setReference] = useState<string | null>("")
  const [deliveryNote, setDeliveryNote] = useState<boolean>(false)
  const [nonStackable, setNonStackable] = useState<boolean>(false)
  const [top, setTop] = useState<boolean>(false)
  const [payer, setPayer] = useState<boolean>(false)
  const [cod, setCod] = useState<boolean>(false)
  const [amount, setAmount] = useState<number | null>(null)
  const [vs, setVs] = useState<number | null>(null)
  const [bankAccount, setBankAccount] = useState<string>("")
  const [note, setNote] = useState<string | null>("")
  const [loadingDate, setLoadingDate] = useState<string>("")
  const [loadingTime, setLoadingTime] = useState<string>("08:00-16:00")
  const [unloadingDate, setUnloadingDate] = useState<string>("")
  const [unloadingTime, setUnloadingTime] = useState<string>("")
  const [customTime, setCustomTime] = useState<string>("08:00-10:00")
  const [workingHours, setWorkingHours] = useState<string>("")
  const [unloadingIn, setUnloadingIn] = useState<string | null>("")
  const [unloadingVatId, setUnloadingVatId] = useState<string | null>("")
  const [isInited, setIsInited] = useState<boolean>(false)
  const [changeCounter, setChangeCounter] = useState<number>(0)
  const [nextStep, setNextStep] = useState<boolean>(false)

  const handleReferenceChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setReference(event.target.value)
  }

  const handleDeliveryNoteChange = (event: CheckboxChangeParams): void => {
    setDeliveryNote(event.checked)
  }

  const handleNonStackableChange = (event: CheckboxChangeParams): void => {
    setNonStackable(event.checked)
  }

  const handleTopChange = (event: CheckboxChangeParams): void => {
    setTop(event.checked)
  }

  const handlePayerChange = (event: RadioButtonChangeParams): void => {
    const value = event.value

    setPayer(value)

    if (value) {
      if (orderDetail) {
        const { unloading } = orderDetail.results.orderDetail

        if (unloading?.in) {
          setUnloadingIn(unloading.in)
        }

        if (unloading?.vatId) {
          setUnloadingVatId(unloading.vatId)
        }
      }
    } else {
      setUnloadingVatId(null)
      setUnloadingIn(null)
    }
  }

  const handleCodChange = (event: CheckboxChangeParams): void => {
    setCod(event.checked)
  }

  const handleAmountChange = (event: InputNumberChangeParams): void => {
    setAmount(event.value)
  }

  const handleVsChange = (event: InputNumberChangeParams): void => {
    setVs(event.value)
  }

  const handleBankAccountChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setBankAccount(event.target.value)
  }

  const handleNoteChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setNote(event.target.value)
  }

  const handleLoadingDateChange = (event: DropdownChangeParams): void => {
    setLoadingDate(event.target.value)
  }

  const handleLoadingTimeChange = (value: string): void => {
    setLoadingTime(value)
  }

  const handleUnloadingDateChange = (event: DropdownChangeParams): void => {
    setUnloadingDate(event.target.value)
  }

  const tUnloadingDateItem = (option: UnloadingTerm): ReactNode => {
    let date = formatDate(option.term)

    return date || option.label
  }

  const tUnloadingDateValue = (option: UnloadingTerm, props: DropdownProps): ReactNode => {
    if (!option) {
      return `${props.placeholder || "empty"}`
    }

    const date = formatDate(option.label)

    return date || option.label
  }

  const handleTimeChange = (event: DropdownChangeParams): void => {
    setUnloadingTime(event.target.value)

    // reset custom time
    const defaultCustomtime = "08:00-10:00"
    setCustomTime(defaultCustomtime)
    setErrors(Object.assign({}, errors, { unloadingTime: "" }))
  }
  const showCustomTime = unloadingTime === TIME_OPTION.CUSTOM

  const handleCustomTimeChange = (value: string) => {
    setCustomTime(value)
    setErrors(Object.assign({}, errors, validateTimeRange(value)))
  }

  const handleInChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUnloadingIn(event.target.value)
  }

  const validateInBlur = () => setErrors(Object.assign({}, errors, validateIn(unloadingIn)))

  const handleVatIdChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUnloadingVatId(event.target.value)
  }
  const validateVatIdBlur = () => setErrors(Object.assign({}, errors, validateVatId(unloadingVatId)))

  const tLoadingDateItem = (option: LoadingTerm): ReactNode => {
    let date = formatDate(option?.term)

    return date || option.label
  }
  const tLoadingDateValue = (option: LoadingTerm, props: DropdownProps): ReactNode => {
    if (!option) {
      return `${props.placeholder || "empty"}`
    }

    const date = formatDate(option.label)

    return date || option.label
  }

  const handleBackClick = (): void => {
    navigate(`/orders/${uuid}/goods`)
  }

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateOrderCalculationPaylod = {
      calculation: {
        reference,
        deliveryNote,
        nonStackable,
        top,
        payer,
        cod: cod ? {
          amount,
          vs,
          bankAccount,
        } : {},
        note,
        loadingDate,
        loadingTime,
        unloadingDate,
        unloadingTime: unloadingTime === TIME_OPTION.CUSTOM ? customTime : unloadingTime,
        unloadingIn,
        unloadingVatId,
      }
    }

    const [isValidCod, errorsCod] = validateCalculationCod(cod, payload)
    setErrorsCod(errorsCod)

    const [isValidForm, errorsForm] = validateCalculation(payload, unloadingTime === TIME_OPTION.CUSTOM)
    setErrors(errorsForm)

    if (isValidCod && isValidForm) {
      await updateCalculation.mutateAsync(payload)
        .then(() => {
          setChangeCounter(0)
          setNextStep(true)
        })
        .catch(({ response }) => {
          let { data }: { data: UpdateOrderCalculationResponseData } = response

          let respErrors: FormErrors<OrderCalculation> = {}
          let respErrorsCod: FormErrors<OrderCalculationCod> = {}

          data.errors.forEach((error) => {
            if (typeof error.property === "string") {
              respErrors[error.property] = error.message
            } else {
              let errorCod = error.property as OrderResponseError<OrderCalculationCod>

              if (typeof errorCod.property === "string") {
                respErrorsCod[errorCod.property] = errorCod.message
              }
            }
          })

          setErrors(respErrors)
          setErrorsCod(respErrorsCod)
          scrollToFirstInvalidElement()
        })
    }
  }

  useEffect(() => {
    if (orderDetail?.results.orderDetail) {
      const { calculation } = orderDetail.results.orderDetail

      if (calculation) {
        setReference(calculation.reference)
        setDeliveryNote(calculation.deliveryNote)
        setNonStackable(calculation.nonStackable)
        setTop(calculation.top)
        setPayer(calculation.payer)

        if (calculation.loadingDate) {
          setLoadingDate(calculation.loadingDate)
        }

        if (calculation.loadingTime) {
          setLoadingTime(calculation.loadingTime)
        }

        if (calculation.unloadingDate) {
          setUnloadingDate(calculation.unloadingDate)
        }

        if (calculation.unloadingTime) {
          setUnloadingTime(calculation.unloadingTime)
        }

        setUnloadingIn(calculation.unloadingIn)
        setUnloadingVatId(calculation.unloadingVatId)

        if (calculation.cod) {
          setCod(Boolean(calculation.cod.amount || calculation.cod.vs || calculation.cod.bankAccount))
          setAmount(calculation.cod.amount || null)
          setVs(calculation.cod.vs || null)
          setBankAccount(calculation.cod.bankAccount || "")
        }

        setNote(calculation.note)
      }

      setIsInited(true)
    }
  }, [orderDetail])

  // fill loading time, vatId and in from loading
  useEffect(() => {
    if (loadingAddressBook) {
      const address = loadingAddressBook.results.addressBookDetail

      if (address.loadingTime) {
        setLoadingTime(address.loadingTime)
      }
    }
  }, [loadingAddressBook])

  // fill unloading time from unloading
  useEffect(() => {
    if (unloadingAddressBook) {
      if (unloadingAddressBook) {
        const address = unloadingAddressBook.results.addressBookDetail
        console.log(address)

        if (address.in) {
          setUnloadingIn(address.in)
        }

        if (address.vatId) {
          setUnloadingVatId(address.vatId)
        }

        if (address.loadingTime) {
          setWorkingHours(address.loadingTime)
        }
      }
    }
  }, [unloadingAddressBook])

  const { data: loadingTerms }: { data: LoadingTermsResponseData | undefined } = useLoadingTerms({
    countryCode: orderDetail?.results.orderDetail.loading?.country, date: today
  }, isEditable)

  const { data: unloadingTerms }: { data: UnloadingTermsResponseData | undefined } =
    useUnloadingTerms({
      unloadingCountryCode: orderDetail?.results.orderDetail.unloading?.country,
      loadingCountryCode: orderDetail?.results.orderDetail.loading?.country,
      loadingDate,
    }, isEditable)

  const { data: unloadingTimes }: { data: UnloadingTimesResponseData | undefined } =
    useUnloadingTimes({
      unloadingDate: unloadingDate,
      unloadingCountryCode: orderDetail?.results.orderDetail.unloading?.country,
      unloadingZIP: orderDetail?.results.orderDetail.unloading?.zip,
      loadingCountryCode: orderDetail?.results.orderDetail.loading?.country,
    }, isEditable)

  useEffect(() => {
    // loading date is not in available options anymore
    if (
      isInited &&
      isEditable &&
      loadingDate &&
      loadingTerms &&
      !loadingTerms.results.loadingTermList.some((loadingTerm) => loadingTerm.term === loadingDate)
    ) {
      setLoadingDate("")
      setUnloadingDate("")
      setUnloadingTime("")
    }

    // prefill first loading date if no one is available
    if (
      isInited &&
      isEditable &&
      !loadingDate &&
      loadingTerms &&
      loadingTerms.results.loadingTermList.length > 0
    ) {
      setLoadingDate(loadingTerms.results.loadingTermList[0].term)
      setUnloadingDate("")
      setUnloadingTime("")
    }
  }, [loadingDate, loadingTerms, isEditable, isInited])

  // prefill first unloading date
  useEffect(() => {
    if (
      isInited &&
      isEditable &&
      !unloadingDate &&
      unloadingTerms &&
      unloadingTerms.results.unloadingTermList.length > 0
    ) {
      setUnloadingDate(unloadingTerms.results.unloadingTermList[0].term)
    }
  }, [unloadingDate, unloadingTerms, isEditable, isInited])

  // prefill first unloading date when loading date change and current unloading date is not available anymore
  useEffect(() => {
    if (
      isInited &&
      isEditable &&
      loadingDate &&
      unloadingDate &&
      unloadingTerms &&
      unloadingTerms.results.unloadingTermList.length > 0 &&
      !unloadingTerms.results.unloadingTermList.find((unloadinTerm) => unloadinTerm.term === unloadingDate)
    ) {
      setUnloadingDate(unloadingTerms.results.unloadingTermList[0].term)
    }
  }, [loadingDate, unloadingDate, unloadingTerms, isInited, isEditable])

  // prefill first unloading time
  useEffect(() => {
    if (
      isInited &&
      isEditable &&
      !unloadingTime &&
      unloadingTimes &&
      unloadingTimes.results.unloadingTimeList.length > 0
    ) {
      setUnloadingTime(unloadingTimes.results.unloadingTimeList[0].time)
    }
  }, [unloadingTime, unloadingTimes, isEditable, isInited])

  // custom time
  useEffect(() => {
    if (
      isEditable &&
      orderDetail &&
      orderDetail.results.orderDetail &&
      unloadingTime &&
      unloadingTimes &&
      !unloadingTimes.results.unloadingTimeList.some((ut) => ut.time === unloadingTime)
    ) {
      setUnloadingTime(orderDetail.results.orderDetail.unloading?.country === "CZ" ? TIME_OPTION.CUSTOM : TIME_OPTION.TBA)
      setCustomTime(unloadingTime)
    }
  }, [unloadingTime, unloadingTimes, orderDetail, isEditable])

  const isForeignCountryTransport = orderDetail?.results.orderDetail.loading?.country !== "CZ" ||
    orderDetail.results.orderDetail.unloading?.country !== "CZ"
  const hasADR = orderDetail?.results.orderDetail.goods?.some((good) => good.adr && good.adr.length > 0)

  // prompt about unsaved form changes
  useEffect(() => {
    if (isInited) {
      setChangeCounter(i => i + 1)
    }
  }, [isInited, reference, deliveryNote, nonStackable, top, payer, amount, vs, bankAccount, note,
    loadingDate, loadingTime, unloadingDate, unloadingTime, unloadingIn, unloadingVatId,])

  usePrompt("Máte neuložené změny. Opravdu chcete odejít?", changeCounter > 1)

  // redirect to next step
  useEffect(() => {
    if (changeCounter <= 1 && nextStep) {
      navigate(`/orders/${uuid}/summary`)
    }
  }, [changeCounter, nextStep, navigate, uuid])

  const infoMessage = (
    <>
      Výše uvedené sekce Kalkulace obsahují příplatkové služby. V závislosti na zvolených
      parametrech mohou být účtovány další poplatky (viz <a
        href={"https://www.dopravanapaletach.cz/web/op.csp"} target={"_blank"} rel={"noreferrer"}
        style={{ color: "inherit" }}>
        obchodní podmínky
      </a>). Celková cena se zobrazí v následujícím kroku Rekapitulace.
    </>
  )

  // prefill COD bank account
  useEffect(() => {
    if (!isInited) {
      return
    }

    let fillBankAccount = (address: Address) => {
      const { bankAccountPrefix, bankAccount, bankCode } = address
      const prefix = bankAccountPrefix ? `${bankAccountPrefix}-` : ""
      setBankAccount(`${prefix}${bankAccount}/${bankCode}`)
    }

    if (cod && isEditable) {
      if (
        !bankAccount &&
        billingAddress?.results.addressDetail.bankAccount
      ) {
        fillBankAccount(billingAddress?.results.addressDetail)
      } else if (
        !bankAccount &&
        mainAddress?.results.addressList &&
        mainAddress?.results.addressList.length > 0 &&
        mainAddress?.results.addressList[0].bankAccount
      ) {
        fillBankAccount(mainAddress?.results.addressList[0])
      }
    } else {
      setBankAccount("")
    }
  }, [cod, isEditable, billingAddress, mainAddress, isInited, bankAccount])

  return (
    <form className={`col-12 ${isEditable ? "" : "p-disabled"}`} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>

        <div className={"py-3"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={reference || ""} onChange={handleReferenceChange}
                  className={classNames({ "p-invalid": !!errors?.reference })} />
                <label>Reference (z. č. objednávky)</label>
              </span>
              <FieldError error={errors?.reference} />
            </div>
          </div>
        </div>

        <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
          Podmínky přepravy
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            {!isForeignCountryTransport && (
              <div className={"field col-12 md:col-3"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"deliveryNote"} inputId={"deliveryNote"}
                    checked={deliveryNote} onChange={handleDeliveryNoteChange}
                    className={classNames({ "p-invalid": !!errors?.deliveryNote })} />
                  <label htmlFor={"deliveryNote"}>Potvrdit dodací list</label>
                </div>
                <FieldError error={errors?.deliveryNote} />
              </div>
            )}

            <div className={"field col-12 md:col-3"}>
              <div className={"field-checkbox"}>
                <Checkbox name={"nonStackable"} inputId={"nonStackable"}
                  checked={nonStackable} onChange={handleNonStackableChange}
                  className={classNames({ "p-invalid": !!errors?.nonStackable })} />
                <label htmlFor={"nonStackable"}>Nestohovat</label>
              </div>
              <FieldError error={errors?.nonStackable} />
            </div>

            {!(isForeignCountryTransport && hasADR) && (
              <div className={"field col-12 md:col-3"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"top"} inputId={"top"}
                    checked={top} onChange={handleTopChange}
                    className={classNames({ "p-invalid": !!errors?.top })} />
                  <label htmlFor={"top"}>TOP přeprava</label>
                </div>
                <FieldError error={errors?.top} />
              </div>
            )}

            <div className={"field col-12"}>
              <label className={"mb-3"}>Přepravu bude hradit:</label>
              <div className={"formgroup-inline"}>
                <div className={"field-radiobutton"}>
                  <RadioButton name={"payer"} inputId={"payer1"} value={false}
                    checked={!payer} onChange={handlePayerChange}
                    className={classNames({ "p-invalid": !!errors?.payer })} />
                  <label htmlFor={"payer1"}>Objednavatel</label>
                </div>
                {!isForeignCountryTransport && (
                  <div className={"field-radiobutton"}>
                    <RadioButton name={"payer"} inputId={"payer2"} value={true}
                      checked={payer} onChange={handlePayerChange}
                      className={classNames({ "p-invalid": !!errors?.payer })} />
                    <label htmlFor={"payer2"}>Příjemce</label>
                  </div>
                )}
              </div>
              <FieldError error={errors?.payer} />
            </div>
          </div>

          <Collapsible isOpen={payer}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={unloadingIn || ""} onChange={handleInChange} onBlur={validateInBlur}
                    className={classNames({ "p-invalid": !!errors?.unloadingIn })}
                    disabled={!payer}
                  />
                  <label>IČ:</label>
                </span>
                <FieldError error={errors?.unloadingIn} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={unloadingVatId || ""} onChange={handleVatIdChange} onBlur={validateVatIdBlur}
                    className={classNames({ "p-invalid": !!errors?.unloadingVatId })}
                    disabled={!payer} />
                  <label>DIČ:</label>
                </span>
                <FieldError error={errors?.unloadingVatId} />
              </div>
            </div>
          </Collapsible>
        </div>

        {!isForeignCountryTransport && (
          <>
            <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
              Dobírka
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
              <div className={"grid"}>
                <div className={"field col-6 md:col-4"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"cod"} inputId={"cod"}
                      checked={cod} onChange={handleCodChange}
                      className={classNames({ "p-invalid": !!errors?.cod })} />
                    <label htmlFor={"cod"}>Dobírka</label>
                  </div>
                  <FieldError error={errors?.cod} />
                </div>
              </div>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputNumber disabled={!cod} value={amount || null} onChange={handleAmountChange}
                      className={classNames({ "p-invalid": !!errorsCod?.amount, "p-disabled": !cod })} />
                    <label className={cod ? commonStyles.labelRequired : ""}>Částka (Kč):</label>
                  </span>
                  <FieldError error={errorsCod?.amount} />
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputNumber disabled={!cod} value={vs || null} onChange={handleVsChange}
                      className={classNames({ "p-invalid": !!errorsCod?.vs, "p-disabled": !cod })} />
                    <label className={cod ? commonStyles.labelRequired : ""}>Variabilní symbol:</label>
                  </span>
                  <FieldError error={errorsCod?.vs} />
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText disabled={!cod} value={bankAccount} onChange={handleBankAccountChange}
                      className={classNames({ "p-invalid": !!errorsCod?.bankAccount })} />
                    <label className={cod ? commonStyles.labelRequired : ""}>Číslo účtu:</label>
                  </span>
                  <FieldError error={errorsCod?.bankAccount} />
                </div>
              </div>
            </div>
          </>
        )}

        <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
          Nakládka
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                {isEditable
                  ? <Dropdown options={loadingTerms?.results.loadingTermList || []}
                    optionLabel={"label"} optionValue={"term"}
                    itemTemplate={tLoadingDateItem} valueTemplate={tLoadingDateValue}
                    value={loadingDate} onChange={handleLoadingDateChange}
                    className={classNames({ "p-invalid": !!errors?.loadingDate })}
                    emptyMessage={"Nic nenalezeno"} />
                  : <InputText value={loadingDate === DATE_OPTION.CLOSEST
                    ? "nejbližší možné"
                    : formatDate(loadingDate) || loadingDate} />
                }
                <label className={commonStyles.labelRequired}>Datum nakládky:</label>
              </span>
              <FieldError error={errors?.loadingDate} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                {isEditable
                  ? <TimeRange onChange={handleLoadingTimeChange} value={loadingTime}
                    error={!!errors.loadingTime} />
                  : <InputText value={loadingTime} />
                }
                <label className={commonStyles.labelRequired}>Čas nakládky:</label>
              </span>
              <FieldError error={errors?.loadingTime} />
            </div>
          </div>
        </div>

        <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
          Vykládka
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                {isEditable
                  ? <Dropdown options={unloadingTerms?.results.unloadingTermList || []}
                    optionLabel={"label"} optionValue={"term"}
                    itemTemplate={tUnloadingDateItem} valueTemplate={tUnloadingDateValue}
                    value={unloadingDate} onChange={handleUnloadingDateChange}
                    className={classNames({ "p-invalid": !!errors?.unloadingDate })}
                    emptyMessage={"Nic nenalezeno"} />
                  : <InputText value={unloadingDate === DATE_OPTION.CLOSEST
                    ? "nejbližší možné"
                    : (formatDate(unloadingDate) || unloadingDate)} />
                }
                <label className={commonStyles.labelRequired}>Datum vykládky:</label>
              </span>
              <FieldError error={errors?.unloadingDate} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                {isEditable
                  ? <Dropdown options={unloadingTimes?.results.unloadingTimeList || []}
                    optionLabel={"label"} optionValue={"time"}
                    value={unloadingTime} onChange={handleTimeChange}
                    className={classNames({ "p-invalid": unloadingTime !== TIME_OPTION.CUSTOM && !!errors?.unloadingTime })}
                    emptyMessage={"Nic nenalezeno"}
                    disabled={isForeignCountryTransport && hasADR} />
                  : <InputText value={unloadingTime} />
                }
                <label className={commonStyles.labelRequired}>Čas vykládky:</label>
              </span>
              {unloadingTime !== TIME_OPTION.CUSTOM && <FieldError error={errors?.unloadingTime} />}
            </div>

            {showCustomTime && (
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <TimeRange value={customTime} onChange={handleCustomTimeChange}
                    disableOptionValidation={true} error={!!errors?.unloadingTime}
                  />
                  <label className={commonStyles.labelRequired}>Vlastní časové okno:</label>
                </span>
                <FieldError error={errors?.unloadingTime} />
              </div>
            )}

            {workingHours && (
              <div className={"field col-12 md:col-4"}>
                <i>Pracovní doba: {workingHours}</i>
              </div>
            )}
          </div>
        </div>

        <div className={"pb-3"}>
          <Message severity={"warn"} text={infoMessage} />
        </div>

        <div className={"pt-3"}>
          <div className={"grid"}>
            <div className={"col-12"}>
              <span className={"p-float-label"}>
                <InputTextarea value={note || ""} onChange={handleNoteChange}
                  className={classNames({ "p-invalid": !!errors?.note })} />
                <label>Poznámka pro DNP:</label>
              </span>
              <FieldError error={errors?.note} />
            </div>
          </div>
        </div>
      </div>

      {isEditable && (
        <div className={`${styles.controls} md:flex`}>
          <div className={"flex-1"}>
            <Button type={"button"} label={"Předchozí krok"}
              className={"p-button p-button-sm p-button-secondary mr-1"}
              icon={"pi pi-arrow-circle-left"}
              disabled={updateCalculation.isLoading}
              onClick={handleBackClick} />
          </div>

          <div className={"flex-1 text-right"}>
            <Button type={"submit"} label={"Další krok"}
              className={"p-button p-button-sm p-button-success"}
              icon={"pi pi-arrow-circle-right"} iconPos={"right"}
              disabled={updateCalculation.isLoading} />
          </div>
        </div>
      )}
    </form>
  )
}
